type CourseData = {
  id: string;
  title: string;
  imageSource: string;
};

type StorageChangeListener = (selectedCourses: Map<string, CourseData>) => void;

/**
 * Centralized store for managing course selection state between multiple controllers.
 * This store maintains the selected course IDs and their associated data,
 * and notifies registered listeners when the state changes.
 */
class CourseSelectionStore {
  private selectedCourses: Map<string, CourseData> = new Map();
  private listeners: Set<StorageChangeListener> = new Set();
  private static instance: CourseSelectionStore | null = null;
  private maxSlots: number | null = null;
  private storageKey = "bbcmaestro_selected_courses";

  private constructor() {
    this.initializeFromStorage();
  }

  static getInstance(): CourseSelectionStore {
    if (!CourseSelectionStore.instance) {
      CourseSelectionStore.instance = new CourseSelectionStore();
    }
    return CourseSelectionStore.instance;
  }

  /**
   * Add a course to the selection
   */
  addCourse(courseData: CourseData, maxSlots?: number): boolean {
    if (this.selectedCourses.has(courseData.id)) {
      return false;
    }

    // Use provided maxSlots or fall back to the stored configuration
    const effectiveMaxSlots = this.getMaxSlots(maxSlots);

    // Check if we've reached the maximum number of slots
    if (effectiveMaxSlots && this.selectedCourses.size >= effectiveMaxSlots) {
      return false;
    }

    this.selectedCourses.set(courseData.id, courseData);
    this.notifyListeners();
    this.saveToStorage();
    this.updateCheckboxStates(effectiveMaxSlots);
    return true;
  }

  /**
   * Remove a course from the selection
   */
  removeCourse(courseId: string, maxSlots?: number): boolean {
    if (!this.selectedCourses.has(courseId)) {
      return false;
    }

    const effectiveMaxSlots = this.getMaxSlots(maxSlots);

    this.selectedCourses.delete(courseId);
    this.notifyListeners();
    this.saveToStorage();

    this.updateCheckboxStates(effectiveMaxSlots);
    return true;
  }

  /**
   * Check if a course is selected
   */
  isCourseSelected(courseId: string): boolean {
    return this.selectedCourses.has(courseId);
  }

  /**
   * Get all selected courses
   */
  getSelectedCourses(): Map<string, CourseData> {
    return new Map(this.selectedCourses);
  }

  /**
   * Get selected course data as an array
   */
  getSelectedCoursesArray(): CourseData[] {
    return Array.from(this.selectedCourses.values());
  }

  /**
   * Get the count of selected courses
   */
  getSelectedCount(): number {
    return this.selectedCourses.size;
  }

  /**
   * Set multiple courses at once (useful for initialization)
   */
  setSelectedCourses(courses: CourseData[], maxSlots?: number): void {
    // Use provided maxSlots or fall back to the stored configuration
    const effectiveMaxSlots = this.getMaxSlots(maxSlots);

    this.selectedCourses.clear();
    courses.forEach((course) => {
      this.selectedCourses.set(course.id, course);
    });
    this.notifyListeners();
    this.saveToStorage();
    this.updateCheckboxStates(effectiveMaxSlots);
  }

  getMaxSlots(maxSlots: number): number | null {
    return maxSlots ?? this.maxSlots;
  }

  /**
   * Register a listener for store changes
   */
  addListener(listener: StorageChangeListener): void {
    this.listeners.add(listener);
  }

  /**
   * Unregister a listener
   */
  removeListener(listener: StorageChangeListener): void {
    this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      listener(this.getSelectedCourses());
    });
  }

  /**
   * Initialize the store from existing form state (checkboxes)
   * This should be called once during page load to sync with any pre-checked boxes
   */
  initializeFromDOM(container: Element): void {
    const checkedInputs = container.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][data-id]:checked'
    );

    const courses: CourseData[] = [];
    checkedInputs.forEach((input) => {
      if (
        input.dataset.id &&
        input.dataset.title &&
        input.dataset.imageSource
      ) {
        courses.push({
          id: input.dataset.id,
          title: input.dataset.title,
          imageSource: input.dataset.imageSource,
        });
      }
    });

    this.setSelectedCourses(courses);
  }

  /**
   * Update checkbox disabled states based on selection limit
   */
  private updateCheckboxStates(maxSlots?: number): void {
    const allInputs = document.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][data-id]'
    );

    allInputs.forEach((input) => {
      const isSelected = this.isCourseSelected(input.dataset.id);

      // If no maxSlots provided, enable all unselected checkboxes
      if (!maxSlots) {
        input.disabled = false;
        const item = input.nextElementSibling as HTMLElement;
        item.dataset.state = "enabled";
        return;
      }

      const isAtLimit = this.selectedCourses.size >= maxSlots;

      // Enable if: not at limit OR already selected
      // Disable if: at limit AND not selected
      input.disabled = isAtLimit && !isSelected;

      // Add visual feedback with CSS classes
      const item = input.nextElementSibling as HTMLElement;
      if (input.disabled) {
        item.dataset.state = "disabled";
      } else {
        item.dataset.state = "enabled";
      }
    });
  }

  /**
   * Sync the store state to DOM checkboxes and update their states
   */
  syncToDOM(container: Element, maxSlots?: number): void {
    // Use provided maxSlots or fall back to the stored configuration
    const effectiveMaxSlots = maxSlots ?? this.maxSlots;

    const allInputs = container.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][data-id]'
    );

    allInputs.forEach((input) => {
      if (input.dataset.id) {
        input.checked = this.isCourseSelected(input.dataset.id);
      }
    });

    // Update checkbox states if maxSlots is provided
    if (effectiveMaxSlots) {
      this.updateCheckboxStates(effectiveMaxSlots);
    }
  }

  ensureProperStyling(container: Element): void {
    // First sync the DOM checkboxes with our stored state
    this.syncToDOM(container, this.maxSlots);

    // Then ensure styling is applied (this handles the localStorage refresh case)
    if (this.maxSlots == this.selectedCourses.size) {
      this.updateCheckboxStates(this.maxSlots);
    }
  }

  /**
   * Set the maximum number of slots (called by the main controller)
   */
  setMaxSlots(maxSlots: number): void {
    this.maxSlots = maxSlots;
  }

  private initializeFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        data.forEach((course: CourseData) => {
          this.selectedCourses.set(course.id, course);
        });
      }
    } catch (error) {
      /* eslint-disable no-console */
      console.log("Failed to load course selection from localStorage");
      this.clearStorage();
    }
  }

  private saveToStorage(): void {
    try {
      const data = Array.from(this.selectedCourses.values());
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      /* eslint-disable no-console */
      console.log("Failed to save course selection to localStorage");
    }
  }

  clearStorage(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      /* eslint-disable no-console */
      console.log("Failed to clear course selection from localStorage");
    }
  }
}

export { CourseSelectionStore, type CourseData, type StorageChangeListener };
